import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

interface Props {
  open: boolean;
  itemCount: number;
  total: number;
  onCancel: () => void;
  onConfirm: (waiterPassword?: string) => void;
  /**
   * `settings.waiterConfirm` : un serveur saisit son code (4 chiffres) avant
   * l'envoi — tablette Cart.js:532-552 (clavier numérique). Vérifié côté
   * functions (qrTriggerSubmit).
   */
  requireWaiterPassword?: boolean;
}

export const ConfirmSubmitDialog = ({
  open,
  itemCount,
  total,
  onCancel,
  onConfirm,
  requireWaiterPassword = false,
}: Props): JSX.Element | null => {
  const { t } = useTranslation();
  const confirmRef = useRef<HTMLButtonElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const [password, setPassword] = useState("");
  useEffect(() => {
    if (open) setPassword("");
  }, [open]);
  const passwordReady = !requireWaiterPassword || password.length === 4;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(
      () => (requireWaiterPassword ? passwordRef.current : confirmRef.current)?.focus(),
      50
    );
    return () => clearTimeout(t);
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-submit-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <button
        type="button"
        aria-label={t("common.close", "Fermer")}
        onClick={onCancel}
        className="absolute inset-0 bg-ink/60 animate-fade-in"
      />

      <div className="relative w-full max-w-sm bg-bg rounded-modal shadow-warm-lg p-6 animate-scale-in">
        <h2
          id="confirm-submit-title"
          className="display-font text-2xl font-semibold text-ink leading-tight text-center"
        >
          {t("modalSubmit.title", "Envoyer ma commande ?")}
        </h2>

        <div className="mt-5 bg-pink-softer rounded-card p-4 flex gap-3">
          <span className="text-2xl shrink-0" aria-hidden="true">
            📣
          </span>
          <p className="text-sm text-ink leading-snug">
            <strong>
              {t("modalSubmit.warning", "Toute la commande va être envoyée.")}
            </strong>{" "}
            {t(
              "modalSubmit.warningHint",
              "Assurez-vous que tout le monde à table a fini d'ajouter ses plats avant de confirmer."
            )}
          </p>
        </div>

        <div className="mt-5 px-2">
          <div className="flex justify-between text-sm">
            <span className="text-ink-soft">{t("modalSubmit.items", "Articles")}</span>
            <span className="text-ink font-medium tabular-nums">{itemCount}</span>
          </div>
          <div className="flex justify-between text-sm mt-2">
            <span className="text-ink-soft">{t("modalSubmit.total", "Total")}</span>
            <span className="text-ink font-semibold tabular-nums">{total.toFixed(2)} €</span>
          </div>
        </div>

        {requireWaiterPassword && (
          <div className="mt-5">
            <label htmlFor="waiter-password" className="block text-sm text-ink text-center">
              {t("modalCart.waiterConfirm", "Veuillez appeler un serveur pour validation")}
            </label>
            <input
              ref={passwordRef}
              id="waiter-password"
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              maxLength={4}
              value={password}
              onChange={(e) => setPassword(e.target.value.replace(/\D/g, "").slice(0, 4))}
              className="mt-2 w-full text-center text-2xl tracking-[0.5em] rounded-button border-2 border-border bg-surface py-3 px-4 focus:border-primary outline-none"
              aria-label={t("modalCart.waiterConfirm", "Veuillez appeler un serveur pour validation")}
            />
          </div>
        )}

        <div className="mt-6 flex flex-col-reverse sm:flex-row gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 bg-surface border border-border text-ink rounded-button py-3 px-5 font-medium transition-colors hover:bg-bg"
          >
            {t("modalSubmit.cancel", "Annuler")}
          </button>
          <button
            ref={confirmRef}
            type="button"
            disabled={!passwordReady}
            onClick={() => onConfirm(requireWaiterPassword ? password : undefined)}
            className="flex-1 bg-primary text-white rounded-button py-3 px-5 font-semibold shadow-warm transition-colors hover:bg-primary-hover disabled:opacity-40"
          >
            {t("modalSubmit.confirm", "Confirmer l'envoi")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
